import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import { currentDateFormat } from '../utils';
import { GET_MA_SERVICE, GET_NEMT_SERVICES_V1, GET_SERVICE_AUTH_DETAIL, POST_SERVICE_AUTH } from '../constant';
import SaveButton from '../components/buttons/SaveButton';
import CancelButton from '../components/buttons/CancelButton';
import Swal from '../messageBox/customSweetAlert';
import calender from '../assets/images/calendar.png';

function EditServiceAuthorization() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const startDatepickerRef = useRef(null);
  const endDatepickerRef = useRef(null);
  const queryParams = new URLSearchParams(location.search);
  const encodedProfileId = queryParams.get('profilId');
  const { passengerName, passengerId, payerId } = location.state ?? {};
  const [currentDate] = useState(new Date());
  const [serviceTypeOptions, setServiceTypeOptions] = useState([]);
  const [loadingServiceTypeOptions, setLoadingServiceTypeOptions] = useState(false);
  const [formData, setFormData] = useState({
    sa_number: '',
    trans_type: '',
    service_type: '',
    start_date: null,
    end_date: null,
    sa_status: '1',
    diagnosis_code: '',
    bill_rate: '',
    procedure_code: '',
    milage_code: '',
    modifier: '',
    milage_modifier: '',
    mileage_bill_rate: '',
    base_available_unit: '',
    mileage_available_unit: '',
    remaining_base_code_unit: '',
    remaining_mileage_code_unit: '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const mapNemtServiceOption = (item, index) => {
    const details = item?.serviceDetails ?? {};
    const modifier = details?.modifier;
    const milage_modifier = details?.milage_modifier;
    return {
      service_rate_id: item?.id ?? null,
      id: details?.id ?? index,
      service_name: details?.service_name ?? '',
      base_code: details?.base_code ?? '',
      milage_code: details?.milage_code ?? '',
      modifier: modifier != null && modifier !== '' ? String(modifier) : '',
      milage_modifier: milage_modifier != null && milage_modifier !== '' ? String(milage_modifier) : '',
      base_bill_rate: item?.base_bill_rate ?? '',
      mileage_bill_rate: item?.mileage_bill_rate ?? '',
    };
  };

  const fetchServiceTypeOptions = async () => {
    try {
      if (!payerId) {
        setServiceTypeOptions([]);
        setLoadingServiceTypeOptions(false);
        return;
      }
      setLoadingServiceTypeOptions(true);
      const response = await axios.get(`${GET_NEMT_SERVICES_V1}?payer=${payerId}`);
      const isOk =
        response?.data?.success === true ||
        response?.data?.status === 200 ||
        response?.data?.status === '200';

      if (isOk && Array.isArray(response?.data?.data)) {
        setServiceTypeOptions(
          response.data.data.map((item, index) => mapNemtServiceOption(item, index)),
        );
      } else {
        setServiceTypeOptions([]);
      }
    } catch (error) {
      console.error('Error fetching NEMT service type options:', error);
      setServiceTypeOptions([]);
    } finally {
      setLoadingServiceTypeOptions(false);
    }
  };

  useEffect(() => {
    if (formData.trans_type) {
      fetchServiceTypeOptions();
    } else {
      setServiceTypeOptions([]);
    }
  }, [formData.trans_type, payerId]);

  const parseDateFromApi = (value) => {
    if (!value) return null;
    const direct = new Date(value);
    if (!Number.isNaN(direct.getTime())) {
      return direct;
    }
    if (typeof value === 'string') {
      const parts = value.split('/');
      if (parts.length === 3) {
        const [mm, dd, yyyy] = parts;
        const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
        if (!Number.isNaN(d.getTime())) return d;
      }
    }
    return null;
  };

  const resolveTransTypeForService = async (serviceTypeId) => {
    if (serviceTypeId == null) return '2';
    try {
      // const res1 = await axios.get(GET_MA_SERVICE, { params: { id: 1 } });
      // const data1 = res1?.data?.data;
      // if (Array.isArray(data1) && data1.some((d) => (d.nemtService?.id ?? d.id) == serviceTypeId)) {
      //   return '1';
      // }
      const res2 = await axios.get(GET_MA_SERVICE, { params: { id: 2 } });
      const data2 = res2?.data?.data;
      if (Array.isArray(data2) && data2.some((d) => (d.nemtService?.id ?? d.id) == serviceTypeId)) {
        return '2';
      }
    } catch (e) {
      console.warn('Could not resolve trans_type for service:', e);
    }
    return '1';
  };

  const statusNameToId = (name) => {
    const n = (name || '').toString().toUpperCase();
    if (n === 'ACTIVE') return '1';
    if (n === 'INACTIVE') return '2';
    if (n === 'EXPIRED') return '3';
    return '1';
  };

  useEffect(() => {
    const loadDetail = async () => {
      if (!id) return;
      setLoadingDetail(true);
      try {
        const response = await axios.get(`${GET_SERVICE_AUTH_DETAIL}/${id}`);
        const raw = response?.data?.data;
        const item = Array.isArray(raw) ? raw[0] : raw || {};

        const startDateVal = item.start_date ?? item.startDate;
        const endDateVal = item.end_date ?? item.endDate;
        const startDate = parseDateFromApi(startDateVal);
        const endDate = parseDateFromApi(endDateVal);

        let transType = item.trans_type ?? item.transType ?? '';
        const serviceTypeId = item.service_type_id ?? item.serviceTypeId ?? item.serviceType?.id ?? null;
        if (!transType && serviceTypeId != null) {
          transType = await resolveTransTypeForService(serviceTypeId);
        }
        if (!transType) transType = '1';

        const statusName = item.status?.name ?? item.sa_status ?? item.status ?? '';
        const saStatusId = item.sa_status_id ?? item.sa_status ?? statusNameToId(statusName);

        const baseAvailable =
          item.base_available_unit ?? item.base_code_available_unit ?? '';
        const mileageAvailable =
          item.mileage_available_unit ?? item.mileage_code_available_unit ?? '';

        const serviceType = item.serviceType || {};
        const procedureCode = serviceType.base_code ?? '';
        const baseBillRate = item.nemtServiceRate?.base_bill_rate ??
          serviceType.base_bill_rate ??
          '';
        const milageCode = serviceType.milage_code ?? '';
        const mileageBillRate = item.nemtServiceRate?.mileage_bill_rate ??
          serviceType.mileage_bill_rate ??
          '';
        const modifier =
          serviceType.modifier != null && serviceType.modifier !== ''
            ? String(serviceType.modifier)
            : '';
        const milageModifier =
          serviceType.milage_modifier != null && serviceType.milage_modifier !== ''
            ? String(serviceType.milage_modifier)
            : '';
        setFormData((prev) => ({
          ...prev,
          sa_number: String(item.sa_number ?? item.saNumber ?? ''),
          trans_type: String(transType),
          service_type: serviceTypeId != null ? String(serviceTypeId) : '',
          start_date: startDate,
          end_date: endDate,
          sa_status: String(saStatusId),
          diagnosis_code: item.diagnosis_code ?? item.diagnosisCode ?? '',
          procedure_code: procedureCode,
          bill_rate: baseBillRate,
          milage_code: milageCode,
          mileage_bill_rate: mileageBillRate,
          modifier,
          milage_modifier: milageModifier,
          base_available_unit:
            baseAvailable !== null && baseAvailable !== undefined
              ? String(baseAvailable)
              : '',
          mileage_available_unit:
            mileageAvailable !== null && mileageAvailable !== undefined
              ? String(mileageAvailable)
              : '',
          remaining_base_code_unit: item.remaining_base_code_unit ?? '',
          remaining_mileage_code_unit: item.remaining_mileage_code_unit ?? '',
        }));
      } catch (error) {
        console.error('Error loading service authorization detail:', error);
        Swal.fire({
          title: 'Error',
          text:
            error?.response?.data?.message ||
            error?.message ||
            'Failed to load Service Authorization.',
          icon: 'error',
        });
      } finally {
        setLoadingDetail(false);
      }
    };

    loadDetail();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let sanitizedValue = value;
    if (name === 'sa_number') {
      sanitizedValue = value.replace(/\D/g, '').slice(0, 11);
    }
    if (name === 'diagnosis_code') {
      sanitizedValue = value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10);
    }
    if (name === 'base_available_unit' || name === 'mileage_available_unit') {
      sanitizedValue = value.replace(/\D/g, '');
    }
    setFormData((prev) => {
      const next = { ...prev, [name]: sanitizedValue };
      if (name === 'trans_type') {
        next.service_type = '';
        next.procedure_code = '';
        next.milage_code = '';
        next.bill_rate = '';
        next.mileage_bill_rate = '';
        next.modifier = '';
        next.milage_modifier = '';
      }
      if (name === 'service_type') {
        const selectedOption = serviceTypeOptions.find(
          (opt) => String(opt.id) === String(value)
        );
        next.procedure_code = selectedOption?.base_code ?? '';
        next.milage_code = selectedOption?.milage_code ?? '';
        next.bill_rate = selectedOption?.base_bill_rate ?? '';
        next.mileage_bill_rate = selectedOption?.mileage_bill_rate ?? '';
        const mods =
          selectedOption?.modifier != null && selectedOption?.modifier !== ''
            ? [selectedOption.modifier]
            : Array.isArray(selectedOption?.modifiers)
              ? selectedOption.modifiers
              : [];
        const validMods = mods.filter(
          (m) => m != null && String(m).trim() !== ''
        );
        next.modifier =
          validMods.length > 0 ? String(validMods[0]).trim() : '';
        next.milage_modifier =
          validMods.length > 0 ? String(validMods[0]).trim() : '';
      }
      return next;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleStartDateChange = (date) => {
    setFormData((prev) => ({ ...prev, start_date: date }));
    if (errors.start_date) setErrors((prev) => ({ ...prev, start_date: null }));
  };

  const handleEndDateChange = (date) => {
    setFormData((prev) => ({ ...prev, end_date: date }));
    if (errors.end_date) setErrors((prev) => ({ ...prev, end_date: null }));
  };

  const validate = () => {
    const next = {};
    if (!formData.sa_number?.trim()) {
      next.sa_number = 'SA number is required.';
    } else if (!/^\d+$/.test(formData.sa_number.trim())) {
      next.sa_number = 'SA number must contain only digits.';
    } else if (formData.sa_number.trim().length > 11) {
      next.sa_number = 'SA number must be at most 11 digits.';
    }
    if (!formData.trans_type) next.trans_type = 'Trans Type (CC/STS) is required.';
    if (!formData.service_type) next.service_type = 'Service type is required.';
    if (!formData.start_date) next.start_date = 'Start date is required.';
    if (!formData.end_date) next.end_date = 'End date is required.';
    if (
      formData.start_date &&
      formData.end_date &&
      formData.end_date < formData.start_date
    ) {
      next.end_date = 'End date must be on or after start date.';
    }
    if (!formData.sa_status) next.sa_status = 'SA status is required.';
    if (!formData.bill_rate?.trim()) next.bill_rate = 'Bill rate is required.';
    if (!formData.procedure_code?.trim())
      next.procedure_code = 'Procedure code is required.';
    if (
      formData.base_available_unit?.trim() &&
      !/^\d+$/.test(formData.base_available_unit.trim())
    ) {
      next.base_available_unit = 'Available unit must be a number.';
    }
    if (
      formData.mileage_available_unit?.trim() &&
      !/^\d+$/.test(formData.mileage_available_unit.trim())
    ) {
      next.mileage_available_unit = 'Available unit must be a number.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const formatDateForApi = (date) => {
    if (!date || !(date instanceof Date)) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!id) {
      Swal.fire({
        title: 'Error',
        text: 'Missing Service Authorization ID.',
        icon: 'error',
      });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        sa_number: formData.sa_number.trim(),
        passenger_id: Number(passengerId),
        sa_status_id: Number(formData.sa_status),
        start_date: formatDateForApi(formData.start_date),
        end_date: formatDateForApi(formData.end_date),
        service_type_id: Number(formData.service_type),
        base_available_unit: formData.base_available_unit,
        mileage_available_unit: formData.mileage_available_unit,
        diagnosis_code: formData.diagnosis_code,
      };
      const response = await axios.put(`${POST_SERVICE_AUTH}/${id}`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (response?.data?.status === '400') {
        Swal.fire({
          title: 'Error!',
          text:
            response?.data?.message ??
            'Failed to update Service Authorization.',
          icon: 'error',
        });
        setSaving(false);
      }
      if (response?.data?.status === '200') {
        Swal.fire({
          title: 'Success!',
          text: 'Service Authorization updated successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          navigate(
            `/home/passenger/service-authorization?profilId=${encodedProfileId || ''}`,
            {
              state: { passengerName, passengerId, payerId },
            }
          );
        });
      } else {
        Swal.fire({
          title: 'Error',
          text:
            response?.data?.message ||
            'Failed to update Service Authorization.',
          icon: 'error',
        });
        setSaving(false);
      }
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        'Failed to update Service Authorization.';
      Swal.fire({ title: 'Error', text: message, icon: 'error' });
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(
      `/home/passenger/service-authorization?profilId=${encodedProfileId || ''}`,
      {
        state: { passengerName, passengerId, payerId },
      }
    );
  };
  console.log(formData);

  return (
    <div className="tab-pane fade active show" id="edit-service-authorization" role="tabpanel">
      <section>
        <div className="d-flex justify-content-between form-container align-items-center">
          <div>
            <h2 className="fs-5 font-weight-600">Edit Service Authorization</h2>
            {passengerName && (
              <p className="text-muted mb-0 small">Passenger: {passengerName}</p>
            )}
          </div>
          <span>{currentDateFormat(currentDate)}</span>
        </div>
      </section>
      <div className="container-fluid px-mobile-2 bg-white px-5 py-4">
        <form
          onSubmit={handleSubmit}
          style={{ maxWidth: '720px', margin: '0 auto' }}
        >
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="require-field">SA Number</label>
              <input
                type="text"
                className="form-control"
                name="sa_number"
                value={formData.sa_number}
                onChange={handleChange}
                placeholder="Enter SA number"
                maxLength={11}
                inputMode="numeric"
              />
              {errors.sa_number && (
                <span className="errors">{errors.sa_number}</span>
              )}
            </div>
            {/* <div className="col-md-6">
              <label className="require-field">Trans Type (CC/STS)</label>
              <select
                className="form-select"
                name="trans_type"
                value={formData.trans_type}
                onChange={handleChange}
              >
                <option value="">Select Type</option>
                <option value="1">CC</option>
                <option value="2">STS</option>
              </select>
              {errors.trans_type && (
                <span className="errors">{errors.trans_type}</span>
              )}
            </div> */}
            <div className="col-md-6">
              <label className="require-field">Service Type</label>
              <select
                className="form-select"
                name="service_type"
                value={formData.service_type}
                onChange={handleChange}
                disabled={!formData.trans_type || loadingServiceTypeOptions}
              >
                <option value="">
                  {loadingServiceTypeOptions
                    ? 'Loading options...'
                    : 'Select Option'}
                </option>
                {serviceTypeOptions.map((option) => (
                  <option key={option?.service_rate_id ?? option?.id} value={option?.id ?? ''}>
                    {option?.service_name ?? ''}
                  </option>
                ))}
              </select>
              {errors.service_type && (
                <span className="errors">{errors.service_type}</span>
              )}
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="require-field">Start Date</label>
              <div className="d-flex date-picker-wrapper schedule-date align-items-end">
                <DatePicker
                  selected={formData.start_date}
                  onChange={handleStartDateChange}
                  dateFormat="MM/dd/yyyy"
                  autoComplete="off"
                  className="datepicker form-control hasDatepicker"
                  id="start_date_sa"
                  placeholderText="Select start date"
                  ref={startDatepickerRef}
                />
                <button
                  style={{
                    backgroundColor: '#F6F6F6',
                    border: 'none',
                    height: '38px',
                  }}
                  className="btn datepicker-button width-50"
                  type="button"
                  onClick={() =>
                    startDatepickerRef.current?.setOpen?.(true)
                  }
                >
                  <img
                    src={calender}
                    className="img-fluid"
                    width="20"
                    height="20"
                    alt="calendar"
                  />
                </button>
              </div>
              {errors.start_date && (
                <span className="errors">{errors.start_date}</span>
              )}
            </div>
            <div className="col-md-6">
              <label className="require-field">End Date</label>
              <div className="d-flex date-picker-wrapper schedule-date align-items-end">
                <DatePicker
                  selected={formData.end_date}
                  onChange={handleEndDateChange}
                  dateFormat="MM/dd/yyyy"
                  autoComplete="off"
                  className="datepicker form-control hasDatepicker"
                  id="end_date_sa"
                  placeholderText="Select end date"
                  minDate={formData.start_date}
                  ref={endDatepickerRef}
                />
                <button
                  style={{
                    backgroundColor: '#F6F6F6',
                    border: 'none',
                    height: '38px',
                  }}
                  className="btn datepicker-button width-50"
                  type="button"
                  onClick={() =>
                    startDatepickerRef.current?.setOpen?.(true)
                  }
                >
                  <img
                    src={calender}
                    className="img-fluid"
                    width="20"
                    height="20"
                    alt="calendar"
                  />
                </button>
              </div>
              {errors.end_date && (
                <span className="errors">{errors.end_date}</span>
              )}
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="require-field">SA Status</label>
              <select
                className="form-select"
                name="sa_status"
                value={formData.sa_status}
                onChange={handleChange}
              >
                <option value="1">ACTIVE</option>
                <option value="2">INACTIVE</option>
                <option value="3">EXPIRED</option>
              </select>
              {errors.sa_status && (
                <span className="errors">{errors.sa_status}</span>
              )}
            </div>
            <div className="col-md-6">
              <label>Diagnosis Code</label>
              <input
                type="text"
                className="form-control"
                name="diagnosis_code"
                value={formData.diagnosis_code}
                onChange={handleChange}
                placeholder="Enter diagnosis code"
                maxLength={10}
              />
            </div>
          </div>

          {/* Section 3: Base Code & Mileage Code */}
          <div className="row mb-3">
            <div className="col-12">
              <div
                className="border-bottom pb-2 mb-3"
                style={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: '#1e6b5e',
                }}
              >
                Codes &amp; Rates
              </div>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <div
                className="rounded p-3 h-100"
                style={{
                  background: '#f8fafc',
                  border: '1.5px solid #d4dce6',
                }}
              >
                <div
                  className="d-flex align-items-center gap-2 mb-3"
                  style={{
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    letterSpacing: '0.13em',
                    textTransform: 'uppercase',
                    color: '#154d44',
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#1e6b5e',
                    }}
                  />
                  Base Code
                </div>
                <div className="row g-2 mb-2">
                  <div className="col-8">
                    <label className="form-label small fw-semibold">
                      Procedure Code <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="procedure_code"
                      value={formData.procedure_code}
                      readOnly
                      disabled={!formData.service_type}
                    />
                    {errors.procedure_code && (
                      <span className="errors">
                        {errors.procedure_code}
                      </span>
                    )}
                  </div>
                  <div className="col-4">
                    <label className="form-label small fw-semibold">
                      Modifier
                    </label>
                    <select
                      className="form-control form-control-sm"
                      name="modifier"
                      value={formData.modifier}
                      onChange={handleChange}
                      disabled={!formData.service_type}
                    >
                      <option value="">
                        {formData.service_type ? 'None' : '—'}
                      </option>
                      {(() => {
                        const selectedOption = serviceTypeOptions.find(
                          (opt) =>
                            String(opt.id) === String(formData.service_type)
                        );
                        const mods =
                          selectedOption?.modifier != null &&
                          selectedOption?.modifier !== ''
                            ? [selectedOption.modifier]
                            : Array.isArray(selectedOption?.modifiers)
                              ? selectedOption.modifiers
                              : [];
                        const modifierOptions = mods.filter(
                          (m) =>
                            m != null && String(m).trim() !== ''
                        );
                        return modifierOptions.map((m) => (
                          <option key={m} value={String(m).trim()}>
                            {String(m).trim()}
                          </option>
                        ));
                      })()}
                    </select>
                  </div>
                </div>
                <div className="row g-2 mb-2">
                  <div className="col-12">
                    <label className="form-label small fw-semibold">
                      Bill Rate <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="bill_rate"
                      value={formData.bill_rate}
                      readOnly
                      disabled={!formData.service_type}
                      placeholder={
                        formData.service_type
                          ? ''
                          : 'Select Service Type first'
                      }
                    />
                    {errors.bill_rate && (
                      <span className="errors">{errors.bill_rate}</span>
                    )}
                  </div>
                </div>
                <div className="row g-2">
                  <div className="col-12">
                    <label className="form-label small fw-semibold">
                      Available Unit
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="base_available_unit"
                      value={formData.base_available_unit}
                      onChange={handleChange}
                      placeholder="Enter available unit"
                      inputMode="numeric"
                    />
                    {errors.base_available_unit && (
                      <span className="errors">
                        {errors.base_available_unit}
                      </span>
                    )}
                    <small className="text-danger" style={{ fontSize: "13px", fontWeight: "600" }}>Remaining Unit: {formData.remaining_base_code_unit ?? '—'} </small>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div
                className="rounded p-3 h-100"
                style={{
                  background: '#f8fafc',
                  border: '1.5px solid #d4dce6',
                }}
              >
                <div
                  className="d-flex align-items-center gap-2 mb-3"
                  style={{
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    letterSpacing: '0.13em',
                    textTransform: 'uppercase',
                    color: '#154d44',
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#1e6b5e',
                    }}
                  />
                  Mileage Code
                </div>
                <div className="row g-2 mb-2">
                  <div className="col-8">
                    <label className="form-label small fw-semibold">
                      Procedure Code
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="milage_code"
                      value={formData.milage_code}
                      readOnly
                      disabled={!formData.service_type}
                    />
                  </div>
                  <div className="col-4">
                    <label className="form-label small fw-semibold">
                      Modifier
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.milage_modifier}
                      readOnly
                      disabled
                    />
                  </div>
                </div>
                <div className="row g-2 mb-2">
                  <div className="col-12">
                    <label className="form-label small fw-semibold">
                      Bill Rate
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.mileage_bill_rate}
                      readOnly
                      disabled
                    />
                  </div>
                </div>
                <div className="row g-2">
                  <div className="col-12">
                    <label className="form-label small fw-semibold">
                      Available Unit
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="mileage_available_unit"
                      value={formData.mileage_available_unit}
                      onChange={handleChange}
                      placeholder="Enter available unit"
                      inputMode="numeric"
                    />
                    {errors.mileage_available_unit && (
                      <span className="errors">
                        {errors.mileage_available_unit}
                      </span>
                    )}
                    <small className="text-danger" style={{ fontSize: "13px", fontWeight: "600" }}>Remaining Unit: {formData.remaining_mileage_code_unit ?? '—'} </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className="d-flex gap-2 justify-content-center mt-4 w-100"
            style={{ justifyContent: 'center' }}
          >
            <SaveButton
              type="submit"
              buttonTitle="SAVE"
              loading={saving}
              loadingText="Saving..."
            />
            <CancelButton cancelHandler={handleCancel} />
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditServiceAuthorization;

